import React, { useState, useEffect } from 'react';
import apiService from '../utils/api';
import Sidebar from './sidebar/Sidebar';
import './Practices.css';
import Loader from './Loader';
import Snackbar from '@mui/material/Snackbar';
import Delete from '@mui/icons-material/Delete';
import Breadcrumbs from './Breadcrumbs';

const Practices = () => {
  const [practices, setPractices] = useState([]);
  const [filteredPractices, setFilteredPractices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Add modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newPracticeName, setNewPracticeName] = useState('');
  const [newPracticeDescription, setNewPracticeDescription] = useState('');

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editPracticeName, setEditPracticeName] = useState('');
  const [editPracticeDescription, setEditPracticeDescription] = useState('');

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletePracticeId, setDeletePracticeId] = useState(null);

  // Snackbar notifications
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const fetchPractices = async () => {
    try {
      setIsLoading(true);
      const response = await apiService.fetchData('practices');
      const rawData = Array.isArray(response) ? response : (response?.data || []);
      const formatted = rawData.map((practice, index) => ({
        id: practice.id || practice.practice_id,
        name: practice.name || practice.value || '',
        description: practice.description || 'No description available',
        displayIndex: index + 1
      }));
      setPractices(formatted);
      setFilteredPractices(formatted);
    } catch (err) {
      console.error('Error fetching practices:', err);
      setError('Failed to load practices. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPractices();
  }, []);

  useEffect(() => {
    const query = searchQuery.toLowerCase();
    const filtered = practices.filter(p =>
      p.name.toLowerCase().includes(query) ||
      (p.description && p.description.toLowerCase().includes(query))
    );
    setFilteredPractices(filtered);
  }, [searchQuery, practices]);

  const handleAddPractice = async () => {
    if (!newPracticeName.trim()) {
      setSnackbarMessage('Practice name is required');
      setIsSuccess(false);
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        name: newPracticeName.trim(),
        description: newPracticeDescription.trim() || 'No Description',
        created_by: sessionStorage.getItem('user_id') || 'system',
        updated_by: sessionStorage.getItem('user_id') || 'system'
      };

      const response = await apiService.postData('practices', payload);
      if (response.status === 'success' || response.practice_id || response.id) {
        await fetchPractices();
        setNewPracticeName('');
        setNewPracticeDescription('');
        setIsModalOpen(false);
        setSnackbarMessage('Practice added successfully');
        setIsSuccess(true);
        setSnackbarOpen(true);
      } else {
        throw new Error(response.message || 'Failed to add practice');
      }
    } catch (err) {
      console.error('Error adding practice:', err);
      const errorMessage = err.response?.status === 409
        ? 'Practice with this name already exists'
        : (err.response?.data?.message || err.message || 'Failed to add practice. Please try again later.');
      setSnackbarMessage(errorMessage);
      setIsSuccess(false);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEdit = async (id) => {
    if (!editPracticeName.trim()) {
      setSnackbarMessage('Practice name is required');
      setIsSuccess(false);
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsLoading(true);
      const originalPractice = practices.find(p => p.id === id);
      if (
        originalPractice &&
        originalPractice.name === editPracticeName.trim() &&
        originalPractice.description === editPracticeDescription.trim()
      ) {
        setSnackbarMessage('No changes detected');
        setIsSuccess(false);
        setSnackbarOpen(true);
        setIsLoading(false);
        return;
      }

      const payload = {
        name: editPracticeName.trim(),
        description: editPracticeDescription.trim(),
        updated_by: sessionStorage.getItem('user_id') || 'system'
      };

      const response = await apiService.putData(`practices/${id}`, payload);
      if (response.status === 'success' || response.practice_id || response.id) {
        await fetchPractices();
        setSnackbarMessage('Practice updated successfully');
        setIsSuccess(true);
        setSnackbarOpen(true);
        setIsEditModalOpen(false);
        setEditingId(null);
        setEditPracticeName('');
        setEditPracticeDescription('');
      } else {
        throw new Error(response.message || 'Failed to update practice');
      }
    } catch (err) {
      console.error('Error updating practice:', err);
      const errorMessage = err.response?.status === 409
        ? 'Practice with this name already exists'
        : (err.response?.data?.message || err.message || 'Failed to update practice. Please try again later.');
      setSnackbarMessage(errorMessage);
      setIsSuccess(false);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePractice = async (id) => {
    try {
      setIsLoading(true);
      const response = await apiService.deleteData(`practices/${id}`);
      if (response.status === 'success' || response.practice_id) {
        await fetchPractices();
        setSnackbarMessage('Practice deleted successfully');
        setIsSuccess(true);
        setSnackbarOpen(true);
      } else {
        throw new Error(response.message || 'Failed to delete practice');
      }
    } catch (err) {
      console.error('Error deleting practice:', err);
      setSnackbarMessage(err.response?.data?.message || err.message || 'Failed to delete practice. Please try again later.');
      setIsSuccess(false);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
      setIsDeleteModalOpen(false);
      setDeletePracticeId(null);
    }
  };

  return (
    <div className="practices-page-container">
      <Sidebar />
      <div className="practices-page-content">
        <Breadcrumbs />
        <div className="practices-header">
          <h2 style={{ color: '#ff6b35', fontSize: '24px' }}>Practices</h2>
          <button
            className="add-practice-button"
            onClick={() => setIsModalOpen(true)}
          >
            Add Practice
          </button>
        </div>

        {isLoading && practices.length === 0 ? (
          <Loader isLoading={isLoading} message="Loading Practices..." color="#ff6b35" />
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          <>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search practices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <svg
                className="practices-search-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#666"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>

            <table className="practices-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Practice Name</th>
                  <th>Practice Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPractices.length > 0 ? (
                  filteredPractices.map((practice, index) => (
                    <tr key={practice.id}>
                      <td>{index + 1}</td>
                      <td>{practice.name}</td>
                      <td>{practice.description || 'No description available'}</td>
                      <td>
                        <svg
                          className="edit-icon"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#ff6b35"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ cursor: 'pointer', marginRight: '10px', display: 'inline-block', verticalAlign: 'middle' }}
                          title="Edit Practice"
                          onClick={() => {
                            setIsEditModalOpen(true);
                            setEditingId(practice.id);
                            setEditPracticeName(practice.name);
                            setEditPracticeDescription(practice.description === 'No description available' ? '' : practice.description);
                          }}
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        <Delete
                          style={{
                            cursor: 'pointer',
                            width: '20px',
                            height: '20px',
                            color: '#ff6b35',
                            display: 'inline-block',
                            verticalAlign: 'middle',
                          }}
                          titleAccess="Delete Practice"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDeletePracticeId(practice.id);
                            setIsDeleteModalOpen(true);
                          }}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">No practices found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}

        {/* Add Modal */}
        {isModalOpen && (
          <div className="modal-overlay">
            <div className="practices-modal-content">
              <div className="practices-modal-header" style={{ backgroundColor: '#ff6b35', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '50px', paddingLeft: '10px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px' }}>
                <h3 style={{ margin: 0 }}>Add Practice</h3>
              </div>
              <div className="practices-modal-form">
                <label htmlFor="practice-name" className="practice-label">Practice Name</label>
                <input
                  id="practice-name"
                  type="text"
                  value={newPracticeName}
                  onChange={(e) => setNewPracticeName(e.target.value)}
                  placeholder="Enter practice name"
                  className="modal-input"
                />
                <label htmlFor="practice-description" className="practice-label" style={{ marginTop: '12px' }}>Description</label>
                <input
                  id="practice-description"
                  type="text"
                  value={newPracticeDescription}
                  onChange={(e) => setNewPracticeDescription(e.target.value)}
                  placeholder="Enter description"
                  className="modal-input"
                />
              </div>
              <div className="modal-buttons">
                <button
                  className="modal-button save-button"
                  onClick={handleAddPractice}
                >
                  Save
                </button>
                <button
                  className="modal-button cancel-button"
                  onClick={() => {
                    setNewPracticeName('');
                    setNewPracticeDescription('');
                    setIsModalOpen(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {isEditModalOpen && (
          <div className="modal-overlay">
            <div className="practices-modal-content">
              <div className="practices-modal-header" style={{ backgroundColor: '#ff6b35', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '50px', paddingLeft: '10px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px' }}>
                <h3 style={{ margin: 0 }}>Update Practice</h3>
              </div>
              <div className="practices-modal-form">
                <label htmlFor="edit-practice-name" className="practice-label">Practice Name</label>
                <input
                  id="edit-practice-name"
                  type="text"
                  value={editPracticeName}
                  onChange={(e) => setEditPracticeName(e.target.value)}
                  placeholder="Enter practice name"
                  className="modal-input"
                />
                <label htmlFor="edit-practice-description" className="practice-label" style={{ marginTop: '12px' }}>Description</label>
                <input
                  id="edit-practice-description"
                  type="text"
                  value={editPracticeDescription}
                  onChange={(e) => setEditPracticeDescription(e.target.value)}
                  placeholder="Enter description"
                  className="modal-input"
                />
              </div>
              <div className="modal-buttons">
                <button
                  className="modal-button save-button"
                  onClick={() => handleSaveEdit(editingId)}
                >
                  Save
                </button>
                <button
                  className="modal-button cancel-button"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingId(null);
                    setEditPracticeName('');
                    setEditPracticeDescription('');
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && (
          <div className="modal-overlay">
            <div className="practices-modal-content">
              <div className="practices-modal-header" style={{ backgroundColor: '#ff6b35', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '50px', paddingLeft: '10px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px' }}>
                <h3 style={{ margin: 0 }}>Confirm Delete</h3>
              </div>
              <div className="practices-modal-form">
                <p>Do you want to delete this practice?</p>
              </div>
              <div className="modal-buttons">
                <button
                  className="modal-button save-button"
                  onClick={() => handleDeletePractice(deletePracticeId)}
                >
                  Delete
                </button>
                <button
                  className="modal-button cancel-button"
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setDeletePracticeId(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          sx={{
            '& .MuiSnackbarContent-root': {
              backgroundColor: isSuccess ? '#4caf50' : '#f44336',
            },
            zIndex: 1400,
          }}
        />
      </div>
    </div>
  );
};

export default Practices;
